import { Component, Inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { InsuranceCompany, NewInsuranceCompany } from '../../../../interfaces/insurance-company.interface';

export interface InsuranceCompanyDialogData {
  editingInsuranceCompany?: InsuranceCompany | null;
  formData: NewInsuranceCompany;
  isSubmitting?: boolean;
}

export interface InsuranceCompanyDialogResult {
  action: 'save' | 'delete' | 'cancel';
  data?: NewInsuranceCompany;
}

@Component({
  selector: 'app-insurance-company-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
],
  template: `
    <div class="insurance-company-dialog crm-dialog">
      <h2 mat-dialog-title>
        <mat-icon>{{ data.editingInsuranceCompany ? 'edit' : 'business' }}</mat-icon>
        {{ data.editingInsuranceCompany ? 'Biztosító szerkesztése' : 'Új biztosító hozzáadása' }}
      </h2>
    
      <mat-dialog-content>
        <form class="insurance-company-form">
          <!-- Biztosító neve -->
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Biztosító neve</mat-label>
            <input matInput
              [(ngModel)]="data.formData.companyname"
              name="companyname"
              placeholder="pl. Allianz Hungária Zrt."
              [disabled]="data.isSubmitting || false"
              required
              maxlength="255"
              #nameInput="ngModel">
    
            @if (nameInput.invalid && nameInput.touched) {
              <mat-error>
                A biztosító neve kötelező mező
              </mat-error>
            }
          </mat-form-field>
        </form>
      </mat-dialog-content>
    
      <mat-dialog-actions align="end">
        <!-- Törlés gomb bal oldalon -->
        @if (data.editingInsuranceCompany) {
          <button
            mat-button
            color="warn"
            (click)="onDelete()"
            [disabled]="data.isSubmitting || false"
            class="delete-button">
            <mat-icon>delete</mat-icon>
            Törlés
          </button>
        }
    
        <div class="spacer"></div>
    
        <!-- Mégse gomb -->
        <button mat-button
          (click)="onCancel()"
          [disabled]="data.isSubmitting || false">
          Mégse
        </button>
    
        <!-- Mentés gomb -->
        <button mat-raised-button
          color="primary"
          (click)="onSave()"
          [disabled]="!isFormValid() || (data.isSubmitting || false)">
          @if (data.isSubmitting) {
            <mat-spinner diameter="20"></mat-spinner>
          }
          @if (!data.isSubmitting) {
            <mat-icon>{{ data.editingInsuranceCompany ? 'save' : 'add' }}</mat-icon>
          }
          {{ data.editingInsuranceCompany ? 'Mentés' : 'Hozzáadás' }}
        </button>
      </mat-dialog-actions>
    </div>
    `,
  styles: [`
    .insurance-company-dialog {
      min-width: 400px;
      max-width: 600px;
    }

    .insurance-company-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      padding: 16px 0;
      min-height: 120px;
    }

    .full-width {
      width: 100%;
    }

    mat-dialog-title {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      font-size: 1.25rem;
      font-weight: 500;
    }

    mat-dialog-title mat-icon {
      color: var(--primary-color, #1976d2);
    }

    mat-dialog-actions {
      padding: 16px 0 0 0;
      margin: 0;
      gap: 8px;
      display: flex;
      align-items: center;
    }

    .spacer {
      flex: 1;
    }

    .delete-button {
      margin-right: auto;
    }

    mat-dialog-actions button {
      margin: 0 4px;
    }

    mat-dialog-actions button mat-spinner {
      margin-right: 8px;
    }

    mat-dialog-actions button mat-icon {
      margin-right: 8px;
    }

    mat-form-field {
      margin-bottom: 8px;
    }

    .mat-mdc-form-field-hint,
    .mat-mdc-form-field-error {
      font-size: 0.875rem;
    }

    /* Responsive design */
    @media (max-width: 600px) {
      .insurance-company-dialog {
        min-width: 300px;
        max-width: 95vw;
      }
      
      mat-dialog-actions {
        flex-direction: column;
        align-items: stretch;
      }
      
      mat-dialog-actions button {
        margin: 4px 0;
      }
    }
  `]
})
export class InsuranceCompanyDialog {
  constructor(
    public dialogRef: MatDialogRef<InsuranceCompanyDialog>,
    @Inject(MAT_DIALOG_DATA) public data: InsuranceCompanyDialogData
  ) {}

  isFormValid(): boolean {
    return !!(this.data.formData.companyname && this.data.formData.companyname.trim().length > 0);
  }

  onSave(): void {
    if (this.isFormValid()) {
      this.dialogRef.close({
        action: 'save',
        data: {
          companyname: this.data.formData.companyname.trim()
        }
      } as InsuranceCompanyDialogResult);
    }
  }

  onDelete(): void {
    this.dialogRef.close({
      action: 'delete'
    } as InsuranceCompanyDialogResult);
  }

  onCancel(): void {
    this.dialogRef.close({
      action: 'cancel'
    } as InsuranceCompanyDialogResult);
  }
}